import { CloudApiError, cloudFetch } from './http.js'

export function parseEventBlock(block) {
  const event = { id: '', event: 'message', data: '', retry: null }
  const data = []
  for (const line of block.split(/\r?\n/)) {
    if (!line || line.startsWith(':')) continue
    const separator = line.indexOf(':')
    const field = separator >= 0 ? line.slice(0, separator) : line
    const value = separator >= 0 ? line.slice(separator + 1).replace(/^ /, '') : ''
    if (field === 'data') data.push(value)
    else if (field === 'id' && !value.includes('\0')) event.id = value
    else if (field === 'event' && value) event.event = value
    else if (field === 'retry' && /^\d+$/.test(value)) event.retry = Number(value)
  }
  event.data = data.join('\n')
  return event
}

function wait(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer)
      reject(signal.reason || new DOMException('aborted', 'AbortError'))
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort)
      resolve()
    }, milliseconds)
    if (signal?.aborted) abort()
    else signal?.addEventListener('abort', abort, { once: true })
  })
}

async function consumeStream(response, { onEvent, onLastEventId, signal }) {
  if (!response.body) throw new CloudApiError('云端进度通道不可用。', { code: 'SSE_BODY_MISSING' })
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    while (!signal?.aborted) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')
      let boundary = buffer.indexOf('\n\n')
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary + 2)
        const event = parseEventBlock(block)
        if (event.id) onLastEventId(event.id)
        if (event.data || event.event !== 'message') onEvent(event)
        if (event.event === 'stream_complete') return true
        boundary = buffer.indexOf('\n\n')
      }
    }
  } finally {
    await reader.cancel().catch(() => {})
    reader.releaseLock()
  }
}

export function subscribeToCloudEvents({
  path,
  lastEventId = '',
  onEvent,
  onState = () => {},
  signal,
  minRetryMs = 1_500,
  maxRetryMs = 30_000,
}) {
  const controller = new AbortController()
  const combinedSignal = signal ? AbortSignal.any([signal, controller.signal]) : controller.signal
  let currentEventId = lastEventId

  const completed = (async () => {
    let retryMs = minRetryMs
    while (!combinedSignal.aborted) {
      try {
        onState({ state: 'connecting', lastEventId: currentEventId })
        const headers = new Headers({ Accept: 'text/event-stream', 'Cache-Control': 'no-cache' })
        if (currentEventId) headers.set('Last-Event-ID', currentEventId)
        const response = await cloudFetch(path, {
          parseAs: 'response', maxReadRetries: 0,
          headers,
          signal: combinedSignal,
        })
        onState({ state: 'connected', lastEventId: currentEventId })
        const terminal = await consumeStream(response, {
          signal: combinedSignal,
          onEvent: (event) => {
            if (event.retry) retryMs = Math.min(maxRetryMs, Math.max(minRetryMs, event.retry))
            onEvent(event)
          },
          onLastEventId: (value) => { currentEventId = value },
        })
        if (terminal || combinedSignal.aborted) break
        throw new CloudApiError('进度连接已断开，正在重连。', { code: 'SSE_EOF' })
      } catch (error) {
        if (combinedSignal.aborted) break
        if ([401, 403].includes(error?.status)) {
          onState({ state: 'auth_required', lastEventId: currentEventId, error })
          return
        }
        onState({ state: 'reconnecting', lastEventId: currentEventId, retryMs, error })
        try { await wait(retryMs + Math.floor(Math.random() * 400), combinedSignal) }
        catch { break }
        retryMs = Math.min(maxRetryMs, retryMs * 2)
      }
    }
    onState({ state: 'closed', lastEventId: currentEventId })
  })()

  return {
    close: () => controller.abort(new DOMException('subscription closed', 'AbortError')),
    completed,
    get lastEventId() { return currentEventId },
  }
}
